import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { TrendingUp, TrendingDown, BarChart3, PieChart } from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line, AreaChart, Area
} from "recharts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Raporlar = () => {
  const [ozet, setOzet] = useState(null);
  const [aylikData, setAylikData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ozetRes, aylikRes] = await Promise.all([
        axios.get(`${API}/raporlar/ozet`, { withCredentials: true }),
        axios.get(`${API}/raporlar/aylik`, { withCredentials: true })
      ]);
      setOzet(ozetRes.data);
      setAylikData(aylikRes.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="raporlar-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
            Raporlar
          </h1>
          <p className="text-[#5E5E62] text-sm mt-1">
            Finansal durumunuzu analiz edin
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={18} className="text-[#00C566]" />
              <span className="text-xs uppercase tracking-[0.1em] text-[#5E5E62] font-semibold">Toplam Gelir</span>
            </div>
            <p className="font-mono text-2xl font-medium text-[#00C566]">{formatCurrency(ozet?.toplam_gelir)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown size={18} className="text-[#FF3B30]" />
              <span className="text-xs uppercase tracking-[0.1em] text-[#5E5E62] font-semibold">Toplam Gider</span>
            </div>
            <p className="font-mono text-2xl font-medium text-[#FF3B30]">{formatCurrency(ozet?.toplam_gider)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={18} className="text-[#002FA7]" />
              <span className="text-xs uppercase tracking-[0.1em] text-[#5E5E62] font-semibold">Net Kar/Zarar</span>
            </div>
            <p className={`font-mono text-2xl font-medium ${ozet?.kar_zarar >= 0 ? 'text-[#00C566]' : 'text-[#FF3B30]'}`}>
              {formatCurrency(ozet?.kar_zarar)}
            </p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3">
              <PieChart size={18} className="text-[#FF9500]" />
              <span className="text-xs uppercase tracking-[0.1em] text-[#5E5E62] font-semibold">Cari Bakiye</span>
            </div>
            <p className={`font-mono text-2xl font-medium ${ozet?.cari_bakiye >= 0 ? 'text-[#00C566]' : 'text-[#FF3B30]'}`}>
              {formatCurrency(ozet?.cari_bakiye)}
            </p>
          </div>
        </div>

        {/* Charts */}
        <Tabs defaultValue="gelir-gider" className="space-y-6">
          <TabsList className="bg-[#F7F7F8] p-1">
            <TabsTrigger value="gelir-gider" data-testid="tab-gelir-gider">Gelir/Gider</TabsTrigger>
            <TabsTrigger value="satis" data-testid="tab-satis">Satış Trendi</TabsTrigger>
            <TabsTrigger value="kar" data-testid="tab-kar">Kar/Zarar</TabsTrigger>
          </TabsList>

          <TabsContent value="gelir-gider">
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-6" data-testid="gelir-gider-chart">
              <h3 className="font-heading font-semibold text-[#0A0A0B] mb-6">Aylık Gelir ve Gider Karşılaştırması</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={aylikData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis 
                      dataKey="ay" 
                      tick={{ fontSize: 11, fill: '#5E5E62' }}
                      axisLine={{ stroke: '#E4E4E7' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#5E5E62' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: '#0A0A0B',
                        border: 'none',
                        borderRadius: '2px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                    />
                    <Bar dataKey="gelir" fill="#00C566" name="Gelir" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="gider" fill="#FF3B30" name="Gider" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="satis">
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-6" data-testid="satis-chart">
              <h3 className="font-heading font-semibold text-[#0A0A0B] mb-6">Aylık Satış Trendi</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={aylikData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="satisGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#002FA7" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#002FA7" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis 
                      dataKey="ay" 
                      tick={{ fontSize: 11, fill: '#5E5E62' }}
                      axisLine={{ stroke: '#E4E4E7' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#5E5E62' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: '#0A0A0B',
                        border: 'none',
                        borderRadius: '2px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="satis" 
                      stroke="#002FA7" 
                      strokeWidth={2}
                      fill="url(#satisGradient)"
                      name="Satış"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="kar">
            <div className="bg-white border border-[#E4E4E7] rounded-sm p-6" data-testid="kar-chart">
              <h3 className="font-heading font-semibold text-[#0A0A0B] mb-6">Aylık Kar/Zarar Trendi</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={aylikData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis 
                      dataKey="ay" 
                      tick={{ fontSize: 11, fill: '#5E5E62' }}
                      axisLine={{ stroke: '#E4E4E7' }}
                      tickLine={false}
                    />
                    <YAxis 
                      tick={{ fontSize: 11, fill: '#5E5E62' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{
                        backgroundColor: '#0A0A0B',
                        border: 'none',
                        borderRadius: '2px',
                        color: '#fff',
                        fontSize: '12px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="kar" 
                      stroke="#002FA7" 
                      strokeWidth={2}
                      dot={{ fill: '#002FA7', strokeWidth: 0, r: 4 }}
                      activeDot={{ fill: '#002FA7', strokeWidth: 0, r: 6 }}
                      name="Kar/Zarar"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Detailed Summary Table */}
        <div className="mt-8 bg-white border border-[#E4E4E7] rounded-sm overflow-hidden" data-testid="summary-table">
          <div className="px-6 py-4 border-b border-[#E4E4E7]">
            <h3 className="font-heading font-semibold text-[#0A0A0B]">Detaylı Özet</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="table-header">Kategori</th>
                  <th className="table-header text-right">Tutar</th>
                </tr>
              </thead>
              <tbody>
                <tr className="table-row">
                  <td className="table-cell font-medium">Toplam Gelir</td>
                  <td className="table-cell text-right font-mono text-[#00C566]">{formatCurrency(ozet?.toplam_gelir)}</td>
                </tr>
                <tr className="table-row">
                  <td className="table-cell font-medium">Toplam Gider</td>
                  <td className="table-cell text-right font-mono text-[#FF3B30]">{formatCurrency(ozet?.toplam_gider)}</td>
                </tr>
                <tr className="table-row bg-[#F7F7F8]">
                  <td className="table-cell font-semibold">Net Kar/Zarar</td>
                  <td className={`table-cell text-right font-mono font-semibold ${ozet?.kar_zarar >= 0 ? 'text-[#00C566]' : 'text-[#FF3B30]'}`}>
                    {formatCurrency(ozet?.kar_zarar)}
                  </td>
                </tr>
                <tr className="table-row">
                  <td className="table-cell font-medium">Toplam Satış</td>
                  <td className="table-cell text-right font-mono text-[#002FA7]">{formatCurrency(ozet?.toplam_satis)}</td>
                </tr>
                <tr className="table-row">
                  <td className="table-cell font-medium">Toplam Tahsilat</td>
                  <td className="table-cell text-right font-mono text-[#00C566]">{formatCurrency(ozet?.toplam_tahsilat)}</td>
                </tr>
                <tr className="table-row">
                  <td className="table-cell font-medium">Toplam Alacak (Bekleyen)</td>
                  <td className="table-cell text-right font-mono text-[#5E5E62]">{formatCurrency(ozet?.toplam_alacak)}</td>
                </tr>
                <tr className="table-row">
                  <td className="table-cell font-medium">Toplam Borç (Bekleyen)</td>
                  <td className="table-cell text-right font-mono text-[#5E5E62]">{formatCurrency(ozet?.toplam_borc)}</td>
                </tr>
                <tr className="table-row bg-[#F7F7F8]">
                  <td className="table-cell font-semibold">Cari Bakiye</td>
                  <td className={`table-cell text-right font-mono font-semibold ${ozet?.cari_bakiye >= 0 ? 'text-[#00C566]' : 'text-[#FF3B30]'}`}>
                    {formatCurrency(ozet?.cari_bakiye)}
                  </td>
                </tr>
                {ozet?.vadesi_gecen_adet > 0 && (
                  <tr className="table-row bg-[#FFEBEE]">
                    <td className="table-cell font-medium text-[#FF3B30]">Vadesi Geçen ({ozet?.vadesi_gecen_adet} adet)</td>
                    <td className="table-cell text-right font-mono font-semibold text-[#FF3B30]">{formatCurrency(ozet?.toplam_vadesi_gecen)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Raporlar;
